import {
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
    Dashboard as DashboardIcon,
    School as SchoolIcon,
    Forum as ForumIcon,
    People as PeopleIcon,
    Analytics as AnalyticsIcon,
    Settings as SettingsIcon,
    AutoAwesome as AutoAwesomeIcon,
    UploadFile as UploadFileIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
  } from "@mui/icons-material"
import { WorkshopAdminApi } from "../../../api/workshop-admin-api";
import { WorkshopApi } from "../../../api/workshop-api";
import DttTableData from "../../../components/DttTableData";
import { GridColDef, GridFilterModel, GridLogicOperator, GridRenderCellParams, GridRowSelectionModel, GridSortModel } from "@mui/x-data-grid";
import WorkshopCreation from "./WorkshopCreation";
import { Link, useLocation } from "react-router-dom";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`workshop-tabpanel-${index}`}
      aria-labelledby={`workshop-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Helper function to get chip color based on status
const getStatusColor = (status: string) => {
  switch (status) {
    // Proposal statuses
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'revision':
      return 'warning';
    case 'inactive':
      return 'default';
    case 'awaiting_approval':
      return 'info';
    
    // Workshop statuses
    case 'draft':
      return 'default';
    case 'published':
      return 'success';
    case 'full':
      return 'warning';
    case 'completed':
      return 'primary';
    case 'in_progress':
      return 'info';
    case 'cancelled':
      return 'error';
    
    // Legacy statuses
    case 'Live':
      return 'success';
    case 'Published':
      return 'success';
    case 'Full':
      return 'warning';
    case 'Completed':
      return 'primary';
    case 'Cancelled':
      return 'error';
    
    default: // fallback
      return 'info';
  }
};

// Helper function to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Helper function to format registrations
const formatRegistrations = (count: number | null, max: number | null) => {
  if (count === null || max === null) return '0/0';
  return `${count}/${max}`;
};

// Component để hiển thị nội dung có thể mở rộng
interface ExpandableContentProps {
  content: string;
  maxLength: number;
  title: string;
}

const ExpandableContent: React.FC<ExpandableContentProps> = ({ content, maxLength, title }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!content) return null;
  
  const shouldTruncate = content.length > maxLength;
  const displayContent = isExpanded ? content : content.slice(0, maxLength) + '...';
  
  return (
    <Typography variant="body1" sx={{ mb: 2 }}>
      <strong>{title}:</strong>
      <br />
      {displayContent}
      {shouldTruncate && (
        <Button
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ 
            mt: 1, 
            textTransform: 'none',
            color: '#1976d2',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline'
            }
          }}
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </Button>
      )}
    </Typography>
  );
};

// Component để hiển thị Learning Objectives có thể mở rộng
interface ExpandableLearningObjectivesProps {
  objectives: string;
  maxLines: number;
}

const ExpandableLearningObjectives: React.FC<ExpandableLearningObjectivesProps> = ({ objectives, maxLines }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!objectives) return null;
  
  const lines = objectives.split(/\r?\n|\\n/);
  const shouldTruncate = lines.length > maxLines;
  const displayLines = isExpanded ? lines : lines.slice(0, maxLines);
  
  return (
    <Typography variant="body1" sx={{ mb: 2 }}>
      <strong>Learning Objectives:</strong>
      <br />
      {displayLines.map((line: string, idx: number) => (
        <span key={idx}>{line}<br /></span>
      ))}
      {shouldTruncate && (
        <Button
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ 
            mt: 1, 
            textTransform: 'none',
            color: '#1976d2',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline'
            }
          }}
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </Button>
      )}
    </Typography>
  );
};

// Component để hiển thị lịch sử revision
interface RevisionHistoryProps {
  revisions: any[];
  adminNotes: string;
}

const RevisionHistory: React.FC<RevisionHistoryProps> = ({ revisions, adminNotes }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!revisions || revisions.length === 0) return null;
  
  return (
    <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#dc3545', fontWeight: 600 }}>
        📝 Revision History
      </Typography>
      
      {revisions.map((revision, index) => (
        <Box key={revision.id} sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #dee2e6' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#6c757d' }}>
              Revision #{index + 1}
            </Typography>
            <Typography variant="caption" sx={{ color: '#6c757d' }}>
              {formatDate(revision.created_at)}
            </Typography>
          </Box>
          
          <Typography variant="body2" sx={{ color: '#495057' }}>
            <strong>Reason:</strong> {revision.revision}
          </Typography>
        </Box>
      ))}
      
      {adminNotes && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#856404', mb: 1 }}>
            💬 Latest Notes:
          </Typography>
          <Typography variant="body2" sx={{ color: '#856404' }}>
            {adminNotes}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Component để hiển thị filter tags
interface FilterTagsProps {
  options: Array<{ value: string; label: string; color: string; count: number }>;
  selectedFilter: string;
  onFilterChange: (filter: string) => void;
}

const FilterTags: React.FC<FilterTagsProps> = ({ options, selectedFilter, onFilterChange }) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#495057' }}>
        Filter by Status:
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {options.map((option) => (
          <Chip
            key={option.value}
            label={`${option.label} (${option.count})`}
            onClick={() => onFilterChange(option.value)}
            sx={{
              bgcolor: selectedFilter === option.value ? option.color : 'transparent',
              color: selectedFilter === option.value ? 'white' : option.color,
              border: `1px solid ${option.color}`,
              fontWeight: selectedFilter === option.value ? 600 : 400,
              '&:hover': {
                bgcolor: selectedFilter === option.value ? option.color : `${option.color}20`,
              },
              cursor: 'pointer',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};



export default function Workshops(){
  const location = useLocation();
  const [workshopTab, setWorkshopTab] = useState(() => {
    // Check if we have state from navigation
    if (location.state && location.state.activeTab !== undefined) {
      return location.state.activeTab;
    }
    return 0; // Default to first tab
  });
  
  const handleWorkshopTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setWorkshopTab(newValue);
  };
  const [proposals, setProposals] = useState<any[]>([]);
  const [selectedProposal, setSelectedProposal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [adminNotesDialog, setAdminNotesDialog] = useState<{
    open: boolean;
    type: 'reject' | 'revision' | null;
    notes: string;
  }>({
    open: false,
    type: null,
    notes: '',
  });

  // State cho filter tags
  const [selectedFilter, setSelectedFilter] = useState<string>('awaiting_approval');
  const [filteredProposals, setFilteredProposals] = useState<any[]>([]);
  const [filterOptions, setFilterOptions] = useState([
    { value: 'awaiting_approval', label: 'Awaiting Approval', color: '#17a2b8', count: 0 },
    { value: 'revision', label: 'Revision', color: '#ffc107', count: 0 },
    { value: 'approved', label: 'Approved', color: '#28a745', count: 0 },
    { value: 'rejected', label: 'Rejected', color: '#dc3545', count: 0 },
    { value: 'all', label: 'All', color: '#6c757d', count: 0 },
  ]);

  // Các state cho phần All Workshops
  const [workshopsLoading, setWorkshopsLoading] = useState(false);
  const [workshopData, setWorkshopData] = useState<any[]>([]);
  const [workshopPagination, setWorkshopPagination] = useState({
    current_page: 1,
    total: 0,
    per_page: 10,
    last_page: 1
  });

  // State cho workshop filter
  const [workshopFilter, setWorkshopFilter] = useState<string>('all');
  const [filteredWorkshops, setFilteredWorkshops] = useState<any[]>([]);
  const [workshopFilterOptions, setWorkshopFilterOptions] = useState([
    { value: 'all', label: 'All Workshops', color: '#6c757d', count: 0 },
    { value: 'draft', label: 'Draft', color: '#6c757d', count: 0 },
    { value: 'published', label: 'Published', color: '#28a745', count: 0 },
    { value: 'full', label: 'Full', color: '#ffc107', count: 0 },
    { value: 'completed', label: 'Completed', color: '#1976d2', count: 0 },
    { value: 'in_progress', label: 'In Progress', color: '#17a2b8', count: 0 },
    { value: 'cancelled', label: 'Cancelled', color: '#dc3545', count: 0 },
  ]);




  // Columns cho data grid
  const workshopColumns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 2,
      renderCell: (params) => (
        <Link to={`/workshop-management/workshops/${params.row.id}`} style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 500 }}>
          {params.value}
        </Link>
      )
    },
    {
      field: 'hosts',
      headerName: 'Host',
      flex: 1,
      valueGetter: (params) => {
        // Logic để hiển thị tên host thân thiện hơn
        if (params.row.hosts && typeof params.row.hosts === 'string' && params.row.hosts !== '[]') {
          try {
            const parsedHosts = JSON.parse(params.row.hosts);
            if (parsedHosts.length > 0) {
              const firstHostName = parsedHosts[0].host_name;
              if (parsedHosts.length > 1) {
                return `${firstHostName} and ${parsedHosts.length - 1} other(s)`;
              }
              return firstHostName;
            }
          } catch (e) {
            console.error("Failed to parse hosts:", e);
          }
        }
        // Dữ liệu cũ, trả về host_name
        return params.row.host_name || '';
      },
    },
    {
      field: 'status', // Thay thế 'calculated_status' bằng 'status'
      headerName: 'Status',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        // Dùng logic cũ của bạn để hiển thị trạng thái
        const statusValue = params.row.status === 'published' ? params.row.calculated_status : params.row.status;
        return (
          <Chip
            label={statusValue}
            color={getStatusColor(statusValue)}
            size="small"
          />
        );
      },
    },
    { 
      field: 'date', 
      headerName: 'Date & Time', 
      flex: 1,
      valueGetter: (params) => {
        const date = new Date(params.value);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    { 
      field: 'registrations', 
      headerName: 'Registrations', 
      flex: 1,
      valueGetter: (params) => formatRegistrations(params.row.registrations_count, params.row.max_attendee)
    },
  ];

  // Button options cho data grid
  const buttonOptions = [
    { label: "New Workshop" },
    { label: "Reload Data", icon: <RefreshIcon /> },
  ];

  // Hàm để lọc và đếm proposal theo status
  const filterAndCountProposals = (proposals: any[]) => {
    const counts = {
      all: proposals.length,
      awaiting_approval: proposals.filter(p => p.status === 'awaiting_approval').length,
      revision: proposals.filter(p => p.status === 'revision').length,
      approved: proposals.filter(p => p.status === 'approved').length,
      rejected: proposals.filter(p => p.status === 'rejected').length,
    };

    // Cập nhật counts cho filterOptions
    const updatedFilterOptions = filterOptions.map(option => ({
      ...option,
      count: counts[option.value as keyof typeof counts] || 0
    }));

    // Lọc proposal theo selectedFilter
    const filtered = selectedFilter === 'all' 
      ? proposals 
      : proposals.filter(p => p.status === selectedFilter);

    return { filtered, updatedFilterOptions };
  };

  // Hàm xử lý khi filter thay đổi
  const handleFilterChange = (newFilter: string) => {
    setSelectedFilter(newFilter);
    
    // Lọc lại proposal theo filter mới
    const filtered = newFilter === 'all' 
      ? proposals 
      : proposals.filter(p => p.status === newFilter);
    
    setFilteredProposals(filtered);
    setSelectedProposal(0); // Reset selected proposal
  };

  const fetchProposals = () => {
    setLoading(true);
    WorkshopAdminApi.getListProposal().then((data: any[]) => {
      setProposals(data);
      
      // Lọc và đếm proposal
      const { filtered, updatedFilterOptions } = filterAndCountProposals(data);
      setFilteredProposals(filtered);
      
      // Cập nhật filterOptions với counts mới
      setFilterOptions(updatedFilterOptions);
      
      setLoading(false);
    });
  };

  // Hàm để lọc và đếm workshop theo status
  const filterAndCountWorkshops = (workshops: any[]) => {
    // const counts = {
    //   all: workshops.length,
    //   draft: workshops.filter(w => w.calculated_status === 'draft').length,
    //   published: workshops.filter(w => w.calculated_status === 'published').length,
    //   full: workshops.filter(w => w.calculated_status === 'full').length,
    //   completed: workshops.filter(w => w.calculated_status === 'completed').length,
    //   in_progress: workshops.filter(w => w.calculated_status === 'in_progress').length,
    //   cancelled: workshops.filter(w => w.calculated_status === 'cancelled').length,
    // };

    // // Cập nhật counts cho workshopFilterOptions
    // const updatedWorkshopFilterOptions = workshopFilterOptions.map(option => ({
    //   ...option,
    //   count: counts[option.value as keyof typeof counts] || 0
    // }));

    // // Lọc workshop theo workshopFilter
    // const filtered = workshopFilter === 'all' 
    //   ? workshops 
    //   : workshops.filter(w => w.calculated_status === workshopFilter);

    // return { filtered, updatedWorkshopFilterOptions };
    const counts: { [key: string]: number } = {
      all: workshops.length,
      draft: 0,
      published: 0,
      full: 0,
      completed: 0,
      in_progress: 0,
      cancelled: 0,
    };
  
    workshops.forEach(w => {
      // Luôn tính trạng thái 'draft', 'full', 'cancelled' dựa trên trường 'status'
      if (['draft', 'full', 'cancelled'].includes(w.status)) {
        counts[w.status]++;
      } else if (w.status === 'published') {
        // Khi 'status' là 'published', tính theo 'calculated_status'
        if (counts[w.calculated_status] !== undefined) {
          counts[w.calculated_status]++;
        }
      }
    });
  
    // Cập nhật counts cho workshopFilterOptions
    const updatedWorkshopFilterOptions = workshopFilterOptions.map(option => ({
      ...option,
      count: counts[option.value] || 0
    }));
  
    // Lọc workshop theo workshopFilter
    const filtered = workshopFilter === 'all'
      ? workshops
      : workshops.filter(w => {
        // Logic lọc dựa trên 'workshopFilter'
        if (['draft', 'full', 'cancelled'].includes(workshopFilter)) {
          return w.status === workshopFilter;
        }
        return w.calculated_status === workshopFilter;
      });
  
    return { filtered, updatedWorkshopFilterOptions };
  };

  // Hàm xử lý khi workshop filter thay đổi
  const handleWorkshopFilterChange = (newFilter: string) => {
    // setWorkshopFilter(newFilter);
    
    // // Lọc lại workshop theo filter mới
    // const filtered = newFilter === 'all' 
    //   ? workshopData 
    //   : workshopData.filter(w => w.calculated_status === newFilter);
    
    // setFilteredWorkshops(filtered);

    setWorkshopFilter(newFilter);

    // Áp dụng logic lọc mới
    const filtered = newFilter === 'all'
      ? workshopData
      : workshopData.filter(w => {
          // Lọc theo 'status' cho các trạng thái đã xác định
          if (['draft', 'full', 'cancelled'].includes(newFilter)) {
            return w.status === newFilter;
          }
          // Lọc theo 'calculated_status' cho các trạng thái còn lại
          return w.calculated_status === newFilter;
        });

    setFilteredWorkshops(filtered);
  };

  // Fetch workshops data
  const fetchWorkshops = async (page: number = 1, per_page: number = 10) => {
    setWorkshopsLoading(true);
    try {
      const response = await WorkshopApi.getListWorkshop(1, 10000);
      if (response && response.data) {
        const workshops = response.data.data || [];
        setWorkshopData(workshops);
        
        // Lọc và đếm workshop
        const { filtered, updatedWorkshopFilterOptions } = filterAndCountWorkshops(workshops);
        setFilteredWorkshops(filtered);
        
        // Cập nhật workshopFilterOptions với counts mới
        setWorkshopFilterOptions(updatedWorkshopFilterOptions);
        
        setWorkshopPagination({
          current_page: response.data.current_page || 1,
          total: response.data.total || 0,
          per_page: parseInt(response.data.per_page) || 10,
          last_page: response.data.last_page || 1
        });
      }
    } catch (error) {
      console.error('Error fetching workshops:', error);
      setWorkshopData([]);
      setFilteredWorkshops([]);
    } finally {
      setWorkshopsLoading(false);
    }
  };

  useEffect(() => {
    fetchProposals();
    fetchWorkshops(); // Fetch workshops on component mount
  }, []);

  const handleApprove = async () => {
    if (!filteredProposals[selectedProposal]) return;
    
    setActionLoading(true);
    try {
      await WorkshopAdminApi.approveProposal(filteredProposals[selectedProposal].id);
      alert('Proposal approved successfully!');
      fetchProposals(); // Refresh data
    } catch (error) {
      alert('Failed to approve proposal. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = () => {
    setAdminNotesDialog({ open: true, type: 'reject', notes: '' });
  };

  const handleReconsider = async () => {  
    if (!filteredProposals[selectedProposal]) return;
    
    setActionLoading(true);
    try {
      await WorkshopAdminApi.reconsiderProposal(filteredProposals[selectedProposal].id);
      alert('Proposal reconsidered successfully!');
      fetchProposals(); // Refresh data
    } catch (error) {
      alert('Failed to reconsider proposal. Please try again.');
    } finally {
      setActionLoading(false);
    }
  }

  const handleRequestRevision = () => {
    setAdminNotesDialog({ open: true, type: 'revision', notes: '' });
  };

  const handleSubmitWithNotes = async () => {
    if (!filteredProposals[selectedProposal] || !adminNotesDialog.notes.trim()) {
      alert('Please provide notes for this action.');
      return;
    }

    setActionLoading(true);
    try {
      if (adminNotesDialog.type === 'reject') {
        await WorkshopAdminApi.rejectProposal(
          filteredProposals[selectedProposal].id,
          adminNotesDialog.notes
        );
        alert('Proposal rejected successfully!');
      } else if (adminNotesDialog.type === 'revision') {
        await WorkshopAdminApi.requestRevisionProposal(
          filteredProposals[selectedProposal].id,
          adminNotesDialog.notes
        );
        alert('Revision requested successfully!');
      }
      setAdminNotesDialog({ open: false, type: null, notes: '' });
      fetchProposals(); // Refresh data
    } catch (error) {
      alert('Failed to process action. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const renderActionButtons = () => {
    const proposal = filteredProposals[selectedProposal];
    if (!proposal) return null;

    const status = proposal.status || 'awaiting_approval';

    switch (status) {
      case 'awaiting_approval':
        return (
          <>
            <Button
              variant="contained"
              sx={{ bgcolor: "#2ecc71", "&:hover": { bgcolor: "#27ae60" } }}
              onClick={handleApprove}
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={20} /> : 'Approve'}
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: "#f1c40f",
                color: "#2c3e50",
                "&:hover": { bgcolor: "#f39c12" },
              }}
              onClick={handleRequestRevision}
              disabled={actionLoading}
            >
              Request Revision
            </Button>
            <Button
              variant="contained"
              sx={{ bgcolor: "#e74c3c", "&:hover": { bgcolor: "#c0392b" } }}
              onClick={handleReject}
              disabled={actionLoading}
            >
              Reject
            </Button>
          </>
        );
      
      case 'approved':
        return (
          // <Button
          //   variant="contained"
          //   sx={{ bgcolor: "#95a5a6", "&:hover": { bgcolor: "#7f8c8d" } }}
          //   onClick={() => alert('Mark as inactive functionality not implemented yet')}
          //   disabled={actionLoading}
          // >
          //   Mark Inactive
          // </Button>
          <></>
        );
      
      case 'rejected':
        return (
          <Button
            variant="contained"
            sx={{ bgcolor: "#3498db", "&:hover": { bgcolor: "#2980b9" } }}
            onClick={handleReconsider}
            disabled={actionLoading}
          >
            Reconsider
          </Button>
        );
      
      case 'revision':
        return (
          <>
            <Button
              variant="contained"
              sx={{ bgcolor: "#2ecc71", "&:hover": { bgcolor: "#27ae60" } }}
              onClick={handleApprove}
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={20} /> : 'Approve'}
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: "#f1c40f",
                color: "#2c3e50",
                "&:hover": { bgcolor: "#f39c12" },
              }}
              onClick={handleRequestRevision}
              disabled={actionLoading}
            >
              Request Revision
            </Button>
            <Button
              variant="contained"
              sx={{ bgcolor: "#e74c3c", "&:hover": { bgcolor: "#c0392b" } }}
              onClick={handleReject}
              disabled={actionLoading}
            >
              Reject
            </Button>
          </>
        );
      
      case 'inactive':
        return (
          <Button
            variant="contained"
            sx={{ bgcolor: "#2ecc71", "&:hover": { bgcolor: "#27ae60" } }}
            onClick={() => alert('Mark as active functionality not implemented yet')}
            disabled={actionLoading}
          >
            Activate
          </Button>
        );
      
      default:
        return null;
    }
  };

  // Xử lý menu click cho table workshops
  const handleWorkshopMenuClick = (index: number) => {
    if (index === 0) { // New Workshop
      setWorkshopTab(2); // Switch to Create Workshop tab
    } else if (index === 1) { // Reload Data
      fetchWorkshops(workshopPagination.current_page, workshopPagination.per_page);
    }
  };

  // Xử lý đăng ký workshop
  const handleWorkshopRowClick = (params: any) => {
    // navigate(`/admin/workshops/${params.id}`); // This line was removed
  };



  return (
    <>
      <Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
          Workshops Management
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={workshopTab} onChange={handleWorkshopTabChange}>
            <Tab label="Proposal Queue" />
            <Tab label="All Workshops" />
            <Tab label="Create Official Workshop" />
          </Tabs>
        </Box>

        <TabPanel value={workshopTab} index={0}>
          {/* Filter Tags */}
          <FilterTags
            options={filterOptions}
            selectedFilter={selectedFilter}
            onFilterChange={handleFilterChange}
          />
          
          {/* Reload Data Button */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchProposals}
              disabled={loading}
              sx={{ 
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Reload Data'}
            </Button>
          </Box>
          
          <Grid container spacing={4} sx={{ height: "60vh" }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%", overflow: "auto" }}>
                {loading ? (
                  <Typography align="center" sx={{ mt: 4 }}>Loading...</Typography>
                ) : filteredProposals.length === 0 ? (
                  <Typography align="center" sx={{ mt: 4 }}>
                    {proposals.length === 0 ? 'No proposal found.' : 'No proposals match the selected filter.'}
                  </Typography>
                ) : (
                  <List sx={{ maxHeight: '55vh', overflow: 'auto' }}>
                    {filteredProposals.map((proposal, index) => (
                      <ListItem key={proposal.id} disablePadding>
                        <ListItemButton
                          selected={selectedProposal === index}
                          onClick={() => setSelectedProposal(index)}
                          sx={{ p: 3, borderBottom: "1px solid #dfe4ea" }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{proposal.title}</span>
                                <Chip 
                                  label={proposal.status || 'awaiting_approval'} 
                                  color={getStatusColor(proposal.status || 'awaiting_approval')} 
                                  size="small"
                                  sx={{ textTransform: 'capitalize' }}
                                />
                              </Box>
                            }
                            secondary={`by ${proposal.username} (${proposal.user_email})`}
                            primaryTypographyProps={{ fontWeight: 600 }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card sx={{ p: 4 }}>
                {loading ? (
                  <Typography align="center">Loading...</Typography>
                                ) : filteredProposals.length === 0 ? (
                  <Typography align="center">
                    {proposals.length === 0 ? 'No proposal found.' : 'No proposals match the selected filter.'}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ fontWeight: 600, mb: 3 }}
                    >
                      Proposal Details
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                    >
                      <Avatar sx={{ width: 50, height: 50 }} />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {filteredProposals[selectedProposal]?.username}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#8395a7" }}>
                          {filteredProposals[selectedProposal]?.user_email}
                        </Typography>
                      </Box>
                      <Chip 
                        label={filteredProposals[selectedProposal]?.status || 'awaiting_approval'} 
                        color={getStatusColor(filteredProposals[selectedProposal]?.status || 'awaiting_approval')} 
                        sx={{ ml: 'auto', textTransform: 'capitalize' }}
                      />
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <strong>Title:</strong> {filteredProposals[selectedProposal]?.title}
                    </Typography>
                    
                    <ExpandableContent
                      content={filteredProposals[selectedProposal]?.description || ''}
                      maxLength={200}
                      title="Abstract"
                    />
                    <ExpandableContent
                      content={filteredProposals[selectedProposal]?.target_audience || ''}
                      maxLength={150}
                      title="Target Audience"
                    />
                    <ExpandableLearningObjectives
                      objectives={filteredProposals[selectedProposal]?.learning_objectives || ''}
                      maxLines={3}
                    />
                    
                    {/* Logistics & Settings */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#495057' }}>
                        ⚙️ Logistics & Settings
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Date & Time:</strong> {filteredProposals[selectedProposal]?.date ? 
                              new Date(filteredProposals[selectedProposal].date).toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'Not specified'
                            }
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Duration:</strong> {filteredProposals[selectedProposal]?.duration || 0} minutes
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Format:</strong> 
                            <Chip 
                              label={filteredProposals[selectedProposal]?.format || 'Not specified'} 
                              size="small"
                              sx={{ ml: 1, textTransform: 'capitalize' }}
                            />
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Max Attendees:</strong> {filteredProposals[selectedProposal]?.max_attendee || 'Unlimited'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Event Link:</strong> 
                            {filteredProposals[selectedProposal]?.event_link ? (
                              <a 
                                href={filteredProposals[selectedProposal].event_link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'none', marginLeft: '8px' }}
                              >
                                {filteredProposals[selectedProposal].event_link}
                              </a>
                            ) : (
                              <span style={{ color: '#6c757d', marginLeft: '8px' }}>Not provided</span>
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    
                    {/* Hiển thị lịch sử revision nếu có */}
                    {filteredProposals[selectedProposal]?.status === 'revision' && (
                      <RevisionHistory
                        revisions={filteredProposals[selectedProposal]?.revisions || []}
                        adminNotes={filteredProposals[selectedProposal]?.admin_notes || ''}
                      />
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        pt: 3,
                        borderTop: "1px solid #dfe4ea",
                      }}
                    >
                      {renderActionButtons()}
                    </Box>
                  </>
                )}
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={workshopTab} index={1}>
          {/* Workshop Filter Tags */}
          <FilterTags
            options={workshopFilterOptions}
            selectedFilter={workshopFilter}
            onFilterChange={handleWorkshopFilterChange}
          />
          
          <DttTableData 
            columns={workshopColumns}
            rows={filteredWorkshops}
            loading={workshopsLoading}
            buttonOptions={buttonOptions}
            onMenuClick={handleWorkshopMenuClick}
            checkboxSelection={false}
            disableRowSelectionOnClick={false}
          />
        </TabPanel>

        <TabPanel value={workshopTab} index={2}>
          <WorkshopCreation />
        </TabPanel>
      </Box>

      {/* Admin Notes Dialog */}
      <Dialog 
        open={adminNotesDialog.open} 
        onClose={() => setAdminNotesDialog({ open: false, type: null, notes: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {adminNotesDialog.type === 'reject' ? 'Reject Proposal' : 'Request Revision'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Admin Notes"
            fullWidth
            multiline
            rows={4}
            value={adminNotesDialog.notes}
            onChange={(e) => setAdminNotesDialog({ ...adminNotesDialog, notes: e.target.value })}
            placeholder={
              adminNotesDialog.type === 'reject'
                ? 'Please provide a reason for rejection...'
                : 'Please specify what changes are needed...'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setAdminNotesDialog({ open: false, type: null, notes: '' })}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitWithNotes} 
            variant="contained"
            color={adminNotesDialog.type === 'reject' ? 'error' : 'warning'}
            disabled={actionLoading || !adminNotesDialog.notes.trim()}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>


    </>
  );
}
